import type { AnalyticsConfig, AnalyticsEvent, AnalyticsTransport } from '../types/analytics';

export interface EventTrackerDeps {
  transport: AnalyticsTransport;
  now?: () => number;
  setInterval?: (fn: () => void, ms: number) => number;
  clearInterval?: (id: number) => void;
  addEventListener?: (type: string, fn: () => void) => void;
  removeEventListener?: (type: string, fn: () => void) => void;
}

const DEFAULT_CONFIG: AnalyticsConfig = {
  enabled: true,
  batchSize: 20,
  flushInterval: 30_000,
  debug: false,
};

export class EventTracker {
  private queue: AnalyticsEvent[] = [];
  private config: AnalyticsConfig;
  private deps: EventTrackerDeps;
  private sessionId: string;
  private userId?: string;
  private optedOut = false;
  private flushTimerId: number | null = null;
  private unloadHandler: (() => void) | null = null;

  constructor(
    sessionId: string,
    config?: Partial<AnalyticsConfig>,
    deps?: EventTrackerDeps,
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.deps = deps ?? { transport: { send: async () => {}, sendBeacon: () => true } };
    this.sessionId = sessionId;
    this.startFlushTimer();
    this.listenUnload();
  }

  setUserId(userId: string): void {
    this.userId = userId;
  }

  setOptOut(optOut: boolean): void {
    this.optedOut = optOut;
  }

  isOptedOut(): boolean {
    return this.optedOut;
  }

  track(event: Omit<AnalyticsEvent, 'timestamp' | 'sessionId' | 'userId'>): void {
    if (this.optedOut || !this.config.enabled) return;

    const now = this.deps.now?.() ?? Date.now();
    const fullEvent: AnalyticsEvent = {
      ...event,
      timestamp: now,
      sessionId: this.sessionId,
      userId: this.userId,
    };

    this.queue.push(fullEvent);

    if (this.queue.length >= this.config.batchSize) {
      this.flush();
    }
  }

  trackPageView(path: string): void {
    this.track({
      name: 'page_view',
      category: 'navigation',
      properties: { path },
    });
  }

  trackGameStart(gameId: string): void {
    this.track({
      name: 'game_start',
      category: 'game',
      properties: { gameId },
    });
  }

  trackGameEnd(gameId: string, score: number, duration: number): void {
    this.track({
      name: 'game_end',
      category: 'game',
      properties: { gameId, score, duration },
    });
  }

  trackGameEvent(gameId: string, eventName: string, data?: object): void {
    this.track({
      name: eventName,
      category: 'game',
      properties: { gameId, ...data },
    });
  }

  trackSocial(action: string, target?: string): void {
    this.track({
      name: action,
      category: 'social',
      properties: { target },
    });
  }

  trackError(error: Error, context?: object): void {
    this.track({
      name: 'error',
      category: 'error',
      properties: {
        message: error.message,
        stack: error.stack,
        ...context,
      },
    });
  }

  async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0);
    try {
      await this.deps.transport.send(batch);
    } catch {
      // Re-queue on failure
      this.queue.unshift(...batch);
    }
  }

  flushSync(): void {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0);
    this.deps.transport.sendBeacon(batch);
  }

  getQueueSize(): number {
    return this.queue.length;
  }

  destroy(): void {
    this.stopFlushTimer();
    if (this.unloadHandler && this.deps.removeEventListener) {
      this.deps.removeEventListener('beforeunload', this.unloadHandler);
      this.unloadHandler = null;
    }
    this.flushSync();
  }

  private startFlushTimer(): void {
    const setIntervalFn = this.deps.setInterval ?? (typeof globalThis !== 'undefined' ? globalThis.setInterval : undefined);
    if (!setIntervalFn) return;
    this.flushTimerId = setIntervalFn(() => this.flush(), this.config.flushInterval) as unknown as number;
  }

  private stopFlushTimer(): void {
    if (this.flushTimerId === null) return;
    const clearIntervalFn = this.deps.clearInterval ?? (typeof globalThis !== 'undefined' ? globalThis.clearInterval : undefined);
    clearIntervalFn?.(this.flushTimerId);
    this.flushTimerId = null;
  }

  private listenUnload(): void {
    if (!this.deps.addEventListener) return;
    this.unloadHandler = () => this.flushSync();
    this.deps.addEventListener('beforeunload', this.unloadHandler);
  }
}
